import { mkEmployment } from 'src/services/objects/employment';
import { currentAppSession } from 'src/utilities/storage';
import { mkLoanAppObject } from 'src/utilities/borrowers';
import { mkAssets } from 'src/features/Advisors/Applications/SummaryMortgage/assetContext';
import {
  equals,
  lensPath,
  map,
  pathOr,
  set,
  find,
  join,
  cond,
  always,
  T,
  unionWith,
  eqBy,
  prop,
  values,
  head,
} from 'ramda';
import { compose } from 'recompose';
import { buildLabel } from 'src/utilities/naming';
import { splitAndRetainNumeric, splitTheSavedDataPathWithDot } from '../../ChatEngine/Configs/autoAnswerConfigurator';
import { isNotEmpty, returnIsNotEmptyOrTrue } from 'src/utilities/hasValue';
import { StateStore } from '../../ChatEngine/Configs/apiResouces';
import { LENDSMART_SH_FIELD } from '../intentConstants';
import { mkRealEstateProperty } from 'src/services/objects/realEstateProperty';
import { mkCollaterals } from 'src/services/objects/collaterals';
import { API_RESOURCE_DECLARATIONS } from 'src/services/constants';
import store from 'src/store';
import { timeStampInISOFormat } from 'src/utilities/formatDate';

export const intentMatcher = {
  matcher: (intent: any, arg: any) => equals(intent.key_field, arg.displayer),
  answerKey: 'answer',
};

// TODO: There is no sense why we are using business,borrower,property, lending_term create on this insert context
//need to create a class for this file
export const mkInsertAPIContext = (
  contextData: any,
  apiResource: StateStore,
  apiResourceName: string,
  answer: any,
) => {
  const resources = apiResource.getStateFromStore()
  const { borrower = {}, property = {}, lendingTerm = {}, business = {} } = resources;
  const { loanRoleId, loanAppId } = currentAppSession();
  const loanApp = mkLoanAppObject(
    apiResource.getStateFromStore(),
    loanAppId,
    loanRoleId
  );
  return cond([
    [equals('borrower'), () => updateContextInResource(mkBorrower(borrower, contextData), contextData, answer)],
    [equals('business'), () => updateContextInResource(mkApiResourceData(business, contextData), contextData, answer)],
    [equals('property'), () => updateContextInResource(mkProperty(borrower, property, contextData), contextData, answer)],
    [
      equals('lendingTerms'),
      () => updateContextInResource(mkLendingTerms(lendingTerm, borrower, contextData), contextData, answer),
    ],
    [
      equals('employments'),
      () =>
        updateContextInResource(
          mkEmployment('default', loanApp, loanRoleId, ''),
          contextData,
          answer
        ),
    ],
    [equals('assets'), () => updateContextInResource(mkAssets('default', loanApp, loanRoleId), contextData, answer)],
    [equals('collateral'), () => updateContextInResource(mkCollaterals(loanApp, loanRoleId), contextData, answer)],

    [equals('real_estate_asset'), () => updateContextInResource(mkRealEstateProperty(loanApp, loanRoleId), contextData, answer)],
    [equals('noop_pass'), always(contextData)],
    [equals('noop_fail'), always(contextData)],
    [T, always({})],
  ])(apiResourceName);
};

export const mkUpdateAPIContext = (
  contextFields: any,
  apiResourceName: any,
  apiResource: StateStore,
  answer: any,
  resourceFinder: any,
) => {
  const apiResourceData = apiResource.findResource(resourceFinder, apiResourceName)
  return updateContextInResource(apiResourceData, contextFields, answer, apiResourceName);
};

export const updateContextInResource = (
  apiResourceData: any,
  contextFields: any,
  answer: any = '',
  apiResourceName: any = '',
) => {
  return returnIsNotEmptyOrTrue(
    contextFields,
    setFieldsAndValues(apiResourceData, contextFields, answer, apiResourceName),
    apiResourceData
  )
}

export const resourceFinder = (_resources: any, match_in_api: any) => {
  const matchKey = pathOr('', [buildLabel('api_match_key')], match_in_api);
  const matchValue = pathOr('', [buildLabel('api_match_value')], match_in_api);
  const match_set = splitTheSavedDataPathWithDot(matchKey);
  const isKeyValueMatches = (resource: any) => {
    return equals(pathOr('', match_set, resource), matchValue);
  };
  return find(isKeyValueMatches, _resources || []);
};

const mkLendingTerms = (
  lendingTerm: any,
  borrower: any,
  contextData: any
) => {
  const borrowerName = pathOr('', ['profile', 'name'], borrower);
  const { street, state, city, country, zip_code }: any = pathOr(
    {},
    ['addresses', 'present'],
    borrower
  );
  const buildAddressString = join(',', [
    street,
    state,
    city,
    country,
    zip_code,
  ]);

  return compose(
    set(lensPath(['metadata', 'collateral_owner_name_0']), borrowerName),
    set(
      lensPath(['metadata', 'collateral_owner_address_0']),
      buildAddressString
    )
  )(lendingTerm);
};

const mkProperty = (
  borrower: any,
  properties: any,
  contextData: any
) => {
  const homeAddress = pathOr({}, ['addresses', 'present'], borrower);
  return compose(set(lensPath(['address']), homeAddress))(
    properties
  );
};

const mkBorrower = (borrower: any, contextData: any) => {
  const currentAdd = pathOr({}, ['addresses', 'present'], borrower);
  const resource_path = splitTheSavedDataPathWithDot(
    pathOr('', ['resource_path'], contextData)
  );
  const context_answer = pathOr('', ['answer', 0], contextData);
  const answer = equals(context_answer, 'None of the above')
    ? 'none'
    : context_answer;
  return compose(
    set(lensPath(['addresses', 'mailing_address']), currentAdd),
    set(lensPath(resource_path), answer)
  )(borrower);

};

const mkApiResourceData = (resourceData: any, contextData: any) => {
  const resource_path = splitTheSavedDataPathWithDot(
    pathOr('', ['resource_path'], contextData)
  );
  const context_answer = pathOr('', ['answer', 0], contextData);
  return compose(
    set(lensPath(resource_path), context_answer)
  )(resourceData);
};

/**
 * 
 * @param dataBag contains apiresource,contextdata and intent answer
 * @returns updated apiresource data with context field
 */
const setDeclarationResourceValue = (dataBag: any) => {
  const { data, contextData, answer } = dataBag;
  const setDeclarationFieldValue = (field: any) => {
    return compose(
      set(lensPath(["name"]), pathOr("", [buildLabel("field")], field)),
      set(lensPath(["value"]), getAnswer(field, answer))
    )({})
  }
  const updatedConsent = unionWith(eqBy(prop('name')), map(setDeclarationFieldValue, contextData), pathOr([], ["consents"], data))
  return compose(set(lensPath(["consents"]), updatedConsent))(data);


}

const getAnswer = (field: any, answer: any) => {
  const contextAnswer = pathOr('', [buildLabel('value')], field)
  return cond([
    [
      () => isNotEmpty(contextAnswer) && equals(contextAnswer, 'now()'),
      () => {
        return timeStampInISOFormat();
      },
    ],
    [
      () => isNotEmpty(contextAnswer),
      () => {
        return equals(contextAnswer, 'empty') ? '' : contextAnswer;
      },
    ],
    [
      () => isNotEmpty(pathOr('', [buildLabel('key_field_value')], field)),
      () => {
        const contextkeyFieldAnswer = pathOr('', [buildLabel('key_field_value')], field);
        const intentById = pathOr(
          {},
          ['__resources', 'userSpecificIntent', 'itemsById'],
          store.getState(),
        );
        const intents = pathOr([], ['intents'], head(values(intentById)));
        const intent = find(
          (x) => equals(pathOr('', ['key_field'], x), contextkeyFieldAnswer),
          intents,
        );
        const ans = pathOr('', ['answer'], intent);
        return ans;
      },
    ],
    [T, always(answer)],
  ])();
};

const setFieldsAndValues = (
  data: any,
  contextData: any,
  answer?: any,
  resourceName?: any,
) => {
  if (equals(resourceName, API_RESOURCE_DECLARATIONS)) {
    return setDeclarationResourceValue({ data, contextData, answer })
  }

  const setFieldsValue = (field: any) => {
    return set(
      lensPath(
        splitAndRetainNumeric(pathOr('', [LENDSMART_SH_FIELD], field))
      ),
      getAnswer(field, answer)
    );
  };
  const updFields = map(setFieldsValue, contextData);
  return compose(...updFields)(data);

};
